import React from 'react';
import { Dropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { CrTextField } from '../cr/CrTextField';
import { CrLabel } from './CrLabel';
import { CrDropdown } from './CrDropdown';
import { CrDatePicker } from './CrDatePicker';
import { DateService } from '../../services/DateService';

export interface ICustomFieldDfn {
    ID: number;
    Title: string;
    DataType?: string;  // "Text" | "List" | "MultiSelectList" | "Date" | "DateRange"
    ListValues?: string;
}

export interface ICrCustomFieldValue {
    Key: number;
    Text: string | null;
}

export interface ICrCustomFieldsProps {
    label?: string;
    className?: string;
    customFieldDfns: ICustomFieldDfn[];
    selectedItems?: ICrCustomFieldValue[];
    disabled?: boolean;
    textMaxLength?: number;
    onChange?: (value: ICrCustomFieldValue[]) => void;
}

export const CrCustomFields = ({
    className,
    label,
    customFieldDfns,
    selectedItems,
    disabled,
    textMaxLength,
    onChange
}: ICrCustomFieldsProps): React.ReactElement => {

    const customFieldOptions: IDropdownOption[] = customFieldDfns.map(a => ({
        key: a.ID,
        text: a.Title
    }));

    const _onCustomFieldSelect = (
        event: React.FormEvent<HTMLDivElement>,
        option?: IDropdownOption<any>
    ): void => {
        if (!option) return;
        let updated: ICrCustomFieldValue[] = JSON.parse(JSON.stringify(selectedItems || []));
        if (option.selected) {
            updated.push({ Key: option.key as number, Text: null });
        } else {
            updated = updated.filter(i => i.Key !== option.key);
        }
        onChange && onChange(updated);
    };

    const _onTextChange = (value: string, key: number) => {
        const updated = (selectedItems || []).map(i => i.Key === key ? { ...i, Text: value } : i);
        onChange && onChange(updated);
    };

    const _onListChange = (option: IDropdownOption | undefined, key: number) => {
        if (!option) return;
        const updated = (selectedItems || []).map(i => i.Key === key ? { ...i, Text: option.text } : i);
        onChange && onChange(updated);
    };

    const _onMultiSelectListChange = (
        event: React.FormEvent<HTMLDivElement>,
        option?: IDropdownOption<any>,
        key?: number
    ): void => {
        if (!option || key === undefined) return;
        const current = selectedItems?.find(i => i.Key === key);
        let selectedValues = current?.Text ? current.Text.split(',').map(v => v.trim()) : [];
        if (option.selected) selectedValues.push(option.text);
        else selectedValues = selectedValues.filter(v => v !== option.text);
        const updatedText = selectedValues.join(', ');
        const updated = (selectedItems || []).map(i => i.Key === key ? { ...i, Text: updatedText } : i);
        onChange && onChange(updated);
    };

    const _onDateChange = (date: Date | null, key: number) => {
        const utcValue = date ? date.toISOString() : '';
        const updated = (selectedItems || []).map(i => i.Key === key ? { ...i, Text: utcValue } : i);
        onChange && onChange(updated);
    };

    const _onDateRangeChange = (key: number, startDate?: Date | null, endDate?: Date | null) => {
        const current = selectedItems?.find(i => i.Key === key);
        const [prevStart, prevEnd] = (current?.Text ?? '').split('|');
        const newStart = startDate ? startDate.toISOString() : prevStart || '';
        const newEnd = endDate ? endDate.toISOString() : prevEnd || '';
        const updatedText = `${newStart}|${newEnd}`;
        const updated = (selectedItems || []).map(i => i.Key === key ? { ...i, Text: updatedText } : i);
        onChange && onChange(updated);
    };

    return (
        <div className={className}>
            <Dropdown
                onRenderLabel={() => label ? <CrLabel text={label} icon="Dropdown" /> : null}
                multiSelect
                disabled={disabled}
                options={customFieldOptions}
                selectedKeys={selectedItems?.map(i => i.Key)}
                onChange={_onCustomFieldSelect}
            />

            {selectedItems && selectedItems.map(sel => {
                const attr = customFieldDfns.find(a => a.ID === sel.Key);
                if (!attr) return null;

                const dataType = attr.DataType?.toLowerCase() || "text";
                const listOptions = attr.ListValues
                    ? attr.ListValues.split(',').map(v => ({ key: v.trim(), text: v.trim() }))
                    : [];

                if (dataType === "daterange") {
                    const [startStr, endStr] = (sel.Text ?? '').split('|');
                    const startDate = startStr ? new Date(startStr) : null;
                    const endDate = endStr ? new Date(endStr) : null;

                    return (
                        <div key={sel.Key} style={{ marginLeft: '30px', marginTop: '5px' }}>
                            <CrDatePicker
                                label={`${attr.Title} (Start)`}
                                disabled={disabled}
                                value={startDate ? DateService.removeTimezoneOffset(startDate) : null}
                                onSelectDate={date => _onDateRangeChange(sel.Key, date, null)}
                            />
                            <CrDatePicker
                                label={`${attr.Title} (End)`}
                                disabled={disabled}
                                value={endDate ? DateService.removeTimezoneOffset(endDate) : null}
                                minDate={startDate ? DateService.removeTimezoneOffset(startDate) : undefined}
                                onSelectDate={date => _onDateRangeChange(sel.Key, null, date)}
                            />
                        </div>
                    );
                }

                return (
                    <div key={sel.Key} style={{ marginLeft: '30px', marginTop: '5px' }}>
                        {dataType === "list" && (
                            <CrDropdown
                                label={attr.Title}
                                options={listOptions}
                                disabled={disabled}
                                selectedKey={sel.Text ?? undefined}
                                onChange={(_, o) => _onListChange(o, sel.Key)}
                            />
                        )}

                        {dataType === "multiselectlist" && (
                            <Dropdown
                                label={attr.Title}
                                multiSelect
                                disabled={disabled}
                                options={listOptions}
                                selectedKeys={sel.Text ? sel.Text.split(',').map(v => v.trim()) : []}
                                onChange={(ev, o) => _onMultiSelectListChange(ev, o, sel.Key)}
                            />
                        )}

                        {dataType === "text" && (
                            <CrTextField
                                label={attr.Title}
                                maxLength={textMaxLength}
                                disabled={disabled}
                                value={sel.Text ?? ''}
                                onChange={(e, v) => v !== undefined && _onTextChange(v, sel.Key)}
                            />
                        )}

                        {dataType === "date" && (
                            <CrDatePicker
                                label={attr.Title}
                                disabled={disabled}
                                value={sel.Text ? DateService.removeTimezoneOffset(new Date(sel.Text)) : null}
                                onSelectDate={date => _onDateChange(date, sel.Key)}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
};
